/**
 * 一括編集対象（同じ受注No × 仕入先 × 送り先 × 発送日）関連の純関数。
 */

import { normalizeDateKey } from './requestFormUtils';
import {
  destinationFingerprint,
  fingerprintFromShareRow,
  isDestinationEmpty,
} from './destinationFingerprint';

export { destinationFingerprint, fingerprintFromShareRow, isDestinationEmpty };

/** 分納計画があり、日付の一括対象から外すか */
export function hasSplitPlansForDateBatch(row) {
  return Array.isArray(row?.split_plans) && row.split_plans.length > 0;
}

/** 表示上の発送日キー（YYYY-MM-DD or null） */
export function effectiveShipmentDateKey(row) {
  if (!row) return null;
  return normalizeDateKey(row.latest_shipment_date || row.shipment_date);
}

/**
 * 一括編集キー。
 * 送り先が空の場合は枝番単位の単独キー（他行とマッチしない）。
 */
export function buildClusterKey(row) {
  if (!row) return null;
  const juchuNo = (row.juchu_no == null ? '' : String(row.juchu_no)).trim();
  const supplier = (row.supplier_name == null ? '' : String(row.supplier_name)).trim();
  const shipDate = effectiveShipmentDateKey(row) || '';
  const fp = fingerprintFromShareRow(row);
  if (fp == null) {
    const code = row.juchu_code != null ? String(row.juchu_code) : '';
    const edaban = row.juchu_edaban != null ? String(row.juchu_edaban) : '';
    return `solo:${juchuNo}|${supplier}|${shipDate}|${code}:${edaban}|${row.hacchu_code}-${row.hacchu_datacode}`;
  }
  return `cluster:${juchuNo}|${supplier}|${fp}|${shipDate}`;
}

/** 同じ受注No・同じ送り先（発送日無視）。空送り先は起点行のみ。 */
export function matchesDestinationScope(anchor, row) {
  if (!anchor || !row) return false;
  const aNo = (anchor.juchu_no == null ? '' : String(anchor.juchu_no)).trim();
  const rNo = (row.juchu_no == null ? '' : String(row.juchu_no)).trim();
  if (!aNo || aNo !== rNo) return false;

  const aFp = fingerprintFromShareRow(anchor);
  if (aFp == null) {
    return (
      anchor.juchu_code === row.juchu_code &&
      String(anchor.juchu_edaban) === String(row.juchu_edaban)
    );
  }
  return fingerprintFromShareRow(row) === aFp;
}

/** 日付一括対象になりうるか（分納なし） */
export function isDateSelectableInCluster(row) {
  return !hasSplitPlansForDateBatch(row);
}

/**
 * 一覧 items から、起点行に対するかたまり編集の対象サマリを作る。
 * @param {object} anchor
 * @param {Array} items
 */
export function buildClusterPreview(anchor, items) {
  const list = Array.isArray(items) ? items : [];
  if (!anchor) {
    return {
      clusterRows: [],
      dateCandidates: [],
      destinationRows: [],
      summary: {
        detailCount: 0,
        hacchuCount: 0,
        edabanCount: 0,
        otherShipDateDestinationCount: 0,
        destinationEmpty: true,
      },
    };
  }

  const clusterKey = buildClusterKey(anchor);
  const clusterRows = list.filter((r) => buildClusterKey(r) === clusterKey);
  const destinationRows = list.filter((r) => matchesDestinationScope(anchor, r));
  const dateCandidates = clusterRows.map((r) => ({
    hacchu_code: r.hacchu_code,
    hacchu_changecnt: r.hacchu_changecnt,
    hacchu_datacode: r.hacchu_datacode,
    hacchu_no: r.hacchu_no,
    product_disp: r.product_disp,
    product_name: r.product_name,
    date_selectable: isDateSelectableInCluster(r),
    has_split_plans: hasSplitPlansForDateBatch(r),
  }));

  const clusterShip = effectiveShipmentDateKey(anchor);
  const otherShipDateDestinationCount = destinationRows.filter(
    (r) => effectiveShipmentDateKey(r) !== clusterShip
  ).length;

  const hacchuNos = new Set(
    clusterRows.map((r) => r.hacchu_no).filter((v) => v != null && String(v).trim() !== '')
  );
  const edabanKeys = new Set(
    clusterRows
      .filter((r) => r.juchu_code != null)
      .map((r) => `${r.juchu_code}:${r.juchu_edaban}`)
  );

  return {
    clusterRows,
    dateCandidates,
    destinationRows,
    summary: {
      detailCount: clusterRows.length,
      hacchuCount: hacchuNos.size,
      edabanCount: edabanKeys.size || (clusterRows.length > 0 ? 1 : 0),
      otherShipDateDestinationCount,
      destinationEmpty: fingerprintFromShareRow(anchor) == null,
    },
  };
}

/**
 * 保存後のローカル patch 用: 住所系を同受注・同送り先の行へ適用。
 */
export function patchDestinationFields(items, anchor, fields) {
  if (!Array.isArray(items) || !anchor || !fields) return items;
  return items.map((row) => {
    if (!matchesDestinationScope(anchor, row)) return row;
    return { ...row, ...fields };
  });
}

/**
 * 保存後のローカル patch 用: 日付を選択明細キーへ適用。
 * @param {Array<{hacchu_code:number,hacchu_datacode:number}>} selectedKeys
 */
export function patchDateFields(items, selectedKeys, fields) {
  if (!Array.isArray(items) || !Array.isArray(selectedKeys) || !fields) return items;
  const set = new Set(selectedKeys.map((k) => `${k.hacchu_code}:${k.hacchu_datacode}`));
  return items.map((row) => {
    const key = `${row.hacchu_code}:${row.hacchu_datacode}`;
    if (!set.has(key)) return row;
    const next = { ...row, ...fields };
    if (fields.shipment_date !== undefined) {
      next.latest_shipment_date = fields.shipment_date;
    }
    if (fields.arrival_date !== undefined) {
      next.latest_arrival_date = fields.arrival_date;
      next.arrival_date = fields.arrival_date;
    }
    return next;
  });
}
